/**
 * Billing — admin subscription management. Runs in MOCK mode until real
 * STRIPE_SECRET_KEY / RAZORPAY_KEY_ID env vars are set on the backend.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CreditCard, CheckCircle2, Loader2, Sparkles } from "lucide-react";

const PRICES = {
  USD: { starter: { monthly: 20, annual: 17 * 12 },
         growth:  { monthly: 30, annual: 25 * 12 } },
  INR: { starter: { monthly: 299, annual: 249 * 12 },
         growth:  { monthly: 499, annual: 415 * 12 } },
};

export default function Billing() {
  const { isAdmin } = useAuth();
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cycle, setCycle] = useState("monthly");
  const [currency, setCurrency] = useState(
    (typeof localStorage !== "undefined" && localStorage.getItem("eb_currency")) || "USD",
  );

  const load = async () => {
    try {
      const [s, inv] = await Promise.all([
        api.get("/billing/status"),
        api.get("/billing/invoices").catch(() => ({ data: { invoices: [] } })),
      ]);
      setSub(s.data);
      setInvoices(inv.data.invoices || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const start = async (plan) => {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout",
                               { plan, billing_cycle: cycle, currency, seats: 10 });
      if (r.data.mock) {
        toast.success(`✓ Your ${30}-day free trial has started. No card required.`);
      } else if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? Access continues until the current period ends.")) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Plan cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!isAdmin) {
    return <div className="p-8 text-sm text-muted-foreground" data-testid="billing-forbidden">Admin-only.</div>;
  }

  const activePlan = sub && sub.plan !== "none" ? sub.plan : null;
  const symbol = currency === "INR" ? "₹" : "$";

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="billing-page">
      <header className="flex items-center gap-3">
        <CreditCard className="w-5 h-5 text-sky-500" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-sky-500">Billing</p>
          <h1 className="font-display text-3xl">Plan & payments</h1>
        </div>
        {sub?.mock && (
          <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-amber-500/15 text-amber-500"
                data-testid="billing-mock-badge">
            Mock mode
          </span>
        )}
      </header>

      {/* Current plan */}
      <div className="card-flat p-6" data-testid="billing-current-plan">
        {activePlan ? (
          <>
            <div className="flex items-center gap-3">
              <p className="text-lg font-semibold capitalize">{activePlan}</p>
              <span data-testid="billing-status-badge"
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                      sub.status === "trial" ? "bg-sky-500/15 text-sky-500"
                        : sub.status === "active" ? "bg-emerald-500/15 text-emerald-500"
                        : "bg-zinc-500/15 text-zinc-500"}`}>
                {sub.status}
              </span>
              {sub.status === "trial" && sub.trial_days_left != null && (
                <span className="text-xs text-muted-foreground" data-testid="billing-trial-countdown">
                  {sub.trial_days_left} day{sub.trial_days_left === 1 ? "" : "s"} left in trial
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {sub.currency === "INR" ? "₹" : "$"}{sub.amount?.toLocaleString()} / {sub.billing_cycle} · {sub.seats} seats · renews {sub.current_period_end?.slice(0, 10)}
            </p>
            <div className="flex gap-2 mt-4">
              {activePlan === "starter" && (
                <Button onClick={() => start("growth")} disabled={busy} data-testid="billing-upgrade">
                  Upgrade to Growth
                </Button>
              )}
              {sub.status !== "cancelled" && (
                <Button variant="outline" onClick={cancel} data-testid="billing-cancel">Cancel plan</Button>
              )}
            </div>
          </>
        ) : (
          <div>
            <p className="text-lg font-semibold">You don't have a subscription yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Start a 30-day free trial — no card required.
            </p>
          </div>
        )}
      </div>

      {/* Plan picker (when no active plan) */}
      {!activePlan && (
        <div className="space-y-4" data-testid="billing-plan-picker">
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">Currency</label>
            <select data-testid="billing-currency-select"
                    value={currency} onChange={(e) => setCurrency(e.target.value)}
                    className="px-2 py-1 rounded border border-border/60 bg-background text-sm">
              <option value="USD">USD $</option>
              <option value="INR">INR ₹</option>
            </select>
            <label className="text-xs text-muted-foreground ml-4">Billing</label>
            <div className="flex gap-1">
              {["monthly", "annual"].map((c) => (
                <button key={c}
                        data-testid={`billing-cycle-${c}`}
                        onClick={() => setCycle(c)}
                        className={`px-3 py-1 rounded text-xs ${cycle === c ? "bg-sky-500 text-white" : "border border-border/60"}`}>
                  {c}{c === "annual" && <span className="ml-1 text-emerald-500">save 2mo</span>}
                </button>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {["starter", "growth"].map((plan) => (
              <div key={plan}
                   data-testid={`billing-plan-card-${plan}`}
                   className="card-flat p-5">
                <p className="text-lg font-semibold capitalize">{plan}</p>
                <p className="text-3xl font-display mt-2">
                  {symbol}{PRICES[currency][plan][cycle].toLocaleString()}
                  <span className="text-xs text-muted-foreground ml-1">/{cycle === "annual" ? "yr" : "mo"}</span>
                </p>
                <Button className="mt-4 w-full"
                        onClick={() => start(plan)}
                        disabled={busy}
                        data-testid={`billing-start-${plan}`}>
                  {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Start 30-day free trial
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invoices */}
      <div>
        <h2 className="font-medium mb-2">Payment history</h2>
        <div className="card-flat p-4">
          {invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">No payments yet.</p>
          ) : (
            <table className="w-full text-sm" data-testid="billing-invoices-table">
              <thead className="text-xs text-muted-foreground uppercase tracking-wider">
                <tr><th className="text-left py-1">Date</th><th className="text-left">Event</th><th className="text-right">Amount</th></tr>
              </thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.id} className="border-t border-border/40">
                    <td className="py-1.5">{(i.timestamp || "").slice(0, 10)}</td>
                    <td>{i.event_type}</td>
                    <td className="text-right font-mono">
                      {i.amount ? `${i.currency === "INR" ? "₹" : "$"}${i.amount.toLocaleString()}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
